// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import UploadForm from './components/UploadForm';
import DashboardPage from './pages/DashboardPage';
import ModelInsightsPage from './pages/ModelInsightsPage';
import ForecastPage from './pages/ForecastPage';
import StrategyPage from './pages/StrategyPage';
import CFOToolsPage from './pages/CFOToolsPage';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { track } from './utils/analytics';
import ChatbotPage from './pages/ChatbotPage';
import NotFoundPage from './pages/NotFoundPage';
import LogoNavBar from './components/LogoNavBar';
import AdminAnalyticsPage from './pages/AdminAnalyticsPage';
import { AppProvider } from './context/AppContext';
import './styles/global.css';
import { sendToBackend } from './utils/debug';
import HomePage from './pages/HomePage';
import LoginPage from './auth/LoginPage';
import RegisterPage from './auth/RegisterPage';
import AccountDashboardPage from './pages/AccountDashboardPage';
import SubscriptionPage from './pages/SubscriptionPage';
import PaymentsPage from './pages/PaymentsPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import CheckoutCancelPage from './pages/CheckoutCancelPage';
import LegalPage from './pages/LegalPage';
import StartRegisterPage from './pages/StartRegisterPage';
import AuthCompletePage from './pages/AuthCompletePage';
import ChoosePlanPage from './pages/ChoosePlanPage';
import { useAppContext } from './context/AppContext';
import TransactionsUploadPage from './pages/TransactionsUploadPage';
import FeedbackPage from './pages/FeedbackPage';

sendToBackend('INFO', 'Clairifi App.js loaded');

const setupGlobalDebugging = () => {
  window.onerror = function (msg, url, lineNo, columnNo, error) {
    const message = `[Global Error] ${msg} at ${url}:${lineNo}:${columnNo}`;
    console.error(message, error);
    sendToBackend('ERROR', message, error);
    return false;
  };
  window.onunhandledrejection = function (event) {
    const message = `[Unhandled Promise Rejection] ${event.reason}`;
    console.error(message);
    sendToBackend('ERROR', message);
  };
  console.info('✅ Frontend logger initialized');
  sendToBackend('INFO', 'Frontend logger initialized');
};

const TrialGate = ({ children }) => {
  const { user } = useAppContext();
  const isCore = user?.plan === 'core';
  const trialLocked = !isCore && !!(user?.trial_active && !user?.is_admin);
  return trialLocked ? <Navigate to="/subscription" replace /> : children;
};

const AdminGate = ({ children }) => {
  const { user } = useAppContext();
  return user?.is_admin ? children : <Navigate to="/" replace />;
};

const PageTracker = () => {
  const loc = useLocation();
  const navigate = useNavigate();

  // Track every route change and persist the last path
  useEffect(() => {
    try { track('visit'); } catch {}
    try {
      const full = loc.pathname + (loc.search || '');
      localStorage.setItem('clairifi:lastPath', full);
    } catch {}
  }, [loc.pathname, loc.search]);

  // On first mount only, try to restore last path (avoid loops via a session flag)
  useEffect(() => {
    const key = 'clairifi:lastPathRestored';
    try {
      const already = sessionStorage.getItem(key) === '1';
      if (already) return;

      const saved = localStorage.getItem('clairifi:lastPath');
      const current = loc.pathname + (loc.search || '');
      if (saved && saved !== '/' && saved !== current) {
        sessionStorage.setItem(key, '1');
        navigate(saved, { replace: true });
      } else {
        sessionStorage.setItem(key, '1');
      }
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

const App = () => {
  useEffect(() => {
    setupGlobalDebugging();
    sendToBackend('INFO', 'Clairifi App component mounted');
  }, []);

  // 🔄 Once per session (or every 6h), reconcile plan from server → Stripe
  useEffect(() => {
    const token = localStorage.getItem('clairifi_token') || localStorage.getItem('token');
    if (!token) return;
    const KEY = 'clairifi:lastMembershipSyncTs';
    const last = Number(localStorage.getItem(KEY) || 0);
    if (Date.now() - last < 6 * 60 * 60 * 1000) return; // throttle
    fetch('/api/account/subscription/sync', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    }).finally(() => {
      try { localStorage.setItem(KEY, String(Date.now())); } catch {}
    });
  }, []);

  return (
    <AppProvider>
      <Router>
        <LogoNavBar />
        <PageTracker />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/upload" element={<UploadForm />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/model-insights" element={<TrialGate><ModelInsightsPage /></TrialGate>} />
          <Route path="/forecast" element={<ForecastPage />} />
          <Route path="/strategy" element={<StrategyPage />} />
          <Route path="/transactions-upload" element={<TransactionsUploadPage />} />
          <Route path="/cfo-tools" element={<TrialGate><CFOToolsPage /></TrialGate>} />
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/chatbot" element={<TrialGate><ChatbotPage /></TrialGate>} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/account" element={<AccountDashboardPage />} />
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/payments" element={<PaymentsPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/admin/analytics" element={<AdminGate><AdminAnalyticsPage /></AdminGate>} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="/checkout/cancel" element={<CheckoutCancelPage />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/start" element={<StartRegisterPage />} />
          <Route path="/auth/complete" element={<AuthCompletePage />} />
          <Route path="/onboarding/plan" element={<ChoosePlanPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Router>
    </AppProvider>
  );
};

export default App;
